/**
 * Single source of truth for all setting defaults.
 * Change default values here (e.g. default language = English).
 * Add new domains/keys when features need settings.
 */

import type { TTSSettings, AppearanceSettings, SettingsDomain } from './types';

const DEFAULT_HIGHLIGHT_COLOR_ID = 'yellow';

/** Default values for TTS. Used when nothing is stored (local or account). */
export const TTS_DEFAULTS: Readonly<TTSSettings> = Object.freeze({
  voice: null,
  rate: 0.9,
  volume: 1,
  lang: 'en',
  subtitles: true,
  autoAdvance: true,
  highlightColorId: DEFAULT_HIGHLIGHT_COLOR_ID
});

/** Default values for appearance (e.g. highlight color for TTS/bar/rating). */
export const APPEARANCE_DEFAULTS: Readonly<AppearanceSettings> = Object.freeze({
  highlightColorId: DEFAULT_HIGHLIGHT_COLOR_ID
});

/** All defaults by domain. Add new domains here. */
export const SETTINGS_DEFAULTS = Object.freeze({
  tts: TTS_DEFAULTS,
  appearance: APPEARANCE_DEFAULTS
} as const);

/** Get the default value for a domain key. */
export function getDefault(domain: SettingsDomain, key: string): unknown {
  const domainDefaults = SETTINGS_DEFAULTS[domain];
  if (domainDefaults && key in domainDefaults) {
    return (domainDefaults as Record<string, unknown>)[key];
  }
  return undefined;
}
